import React, { useState, useEffect } from 'react';
import {
  FileText, Plus, Edit2, CheckCircle,
  X, History, Loader2,
  RotateCcw, Check, Eye, EyeOff, Activity, Layers, Code2
} from 'lucide-react';
import { theme } from '../../styles/theme';
import administrationApi, {
  LLMPrompt,
  LLMPromptHistory,
  CreatePromptRequest
} from '../../api/administrationApi';

interface AdminPromptsProps {
  darkMode: boolean;
}

type TabType = 'active' | 'all' | 'variables';
type ModalType = 'create' | 'edit' | 'history' | 'test' | null;

// Template variables configuration
const templateVariables = {
  metrics_extraction: [
    { name: 'metricName', description: 'The name of the metric being collected' },
    { name: 'metricCode', description: 'The unique code identifier for the metric' },
    { name: 'searchName', description: 'The search-optimized name for the metric' },
    { name: 'definition', description: 'The detailed definition of the metric' },
    { name: 'countryName', description: 'The full name of the country' },
    { name: 'countryCode', description: 'The ISO code for the country' },
    { name: 'year', description: 'The target year for data collection' },
    { name: 'minSourcesRequired', description: 'Minimum number of sources required' },
    { name: 'metricGuidance', description: 'Specific guidance for this metric type' },
    { name: 'searchContext', description: 'The formatted search results context' }
  ],
  harmony: [
    { name: 'taskName', description: 'The name of the automation task' },
    { name: 'inputData', description: 'The input data for processing' },
    { name: 'previousStep', description: 'Result from the previous workflow step' },
    { name: 'configuration', description: 'Task-specific configuration' }
  ],
  symphony: [
    { name: 'context', description: 'Context from previous chunks in the document' },
    { name: 'chunk_text', description: 'The current text chunk to analyze' },
    { name: 'document_type', description: 'The type of document being processed' }
  ]
};

const promptTypes = [
  { value: 'metrics_extraction', label: 'Metrics Extraction' },
  { value: 'harmony', label: 'Harmony' },
  { value: 'symphony', label: 'Symphony' }
];

const AdminPrompts: React.FC<AdminPromptsProps> = ({ darkMode }) => {
  const [activeTab, setActiveTab] = useState<TabType>('active');
  const [prompts, setPrompts] = useState<LLMPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalType, setModalType] = useState<ModalType>(null);
  const [selectedPrompt, setSelectedPrompt] = useState<LLMPrompt | null>(null);
  const [promptHistory, setPromptHistory] = useState<LLMPromptHistory[]>([]);
  const [showPromptText, setShowPromptText] = useState<Record<string, boolean>>({});
  
  // Form state
  const [formData, setFormData] = useState<CreatePromptRequest>({
    prompt_type: 'metrics_extraction',
    prompt_name: '',
    prompt_text: '',
    search_method: 'advanced_search'
  });

  // Fetch prompts
  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      const data = await administrationApi.getPrompts();
      setPrompts(data);
    } catch (error) {
      console.error('Failed to fetch prompts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPromptHistory = async (promptId: string) => {
    try {
      console.log('Fetching history for prompt ID:', promptId);
      const history = await administrationApi.getPromptHistory(promptId);
      console.log('Received history data:', history);
      console.log('History array length:', history?.length || 0);
      setPromptHistory(history);
    } catch (error) {
      console.error('Failed to fetch prompt history:', error);
    }
  };

  const handleCreatePrompt = async () => {
    try {
      await administrationApi.createPrompt(formData);
      await fetchPrompts();
      setModalType(null);
      resetForm();
    } catch (error) {
      console.error('Failed to create prompt:', error);
    }
  };

  const handleUpdatePrompt = async () => {
    if (!selectedPrompt) return;
    
    try {
      await administrationApi.updatePrompt(selectedPrompt.id, {
        prompt_name: formData.prompt_name,
        prompt_text: formData.prompt_text
      });
      await fetchPrompts();
      setModalType(null);
      resetForm();
    } catch (error) {
      console.error('Failed to update prompt:', error);
    }
  };

  const handleActivatePrompt = async (id: string) => {
    try {
      await administrationApi.activatePrompt(id);
      await fetchPrompts();
    } catch (error) {
      console.error('Failed to activate prompt:', error);
    }
  };

  const handleDeactivatePrompt = async (id: string) => {
    try {
      await administrationApi.deactivatePrompt(id);
      await fetchPrompts();
    } catch (error) {
      console.error('Failed to deactivate prompt:', error);
    }
  };

  const handleRollback = async (version: number) => {
    if (!selectedPrompt) return;
    
    try {
      await administrationApi.rollbackPrompt(selectedPrompt.id, { target_version: version });
      await fetchPrompts();
      setModalType(null);
    } catch (error) {
      console.error('Failed to rollback prompt:', error);
    }
  };

  const openEditModal = (prompt: LLMPrompt) => {
    setSelectedPrompt(prompt);
    setFormData({
      prompt_type: prompt.prompt_type,
      prompt_name: prompt.prompt_name,
      prompt_text: prompt.prompt_text,
      search_method: prompt.search_method
    });
    setModalType('edit');
  };

  const openHistoryModal = async (prompt: LLMPrompt) => {
    setSelectedPrompt(prompt);
    await fetchPromptHistory(prompt.id);
    setModalType('history');
  };

  const resetForm = () => {
    setFormData({
      prompt_type: 'metrics_extraction',
      prompt_name: '',
      prompt_text: '',
      search_method: 'advanced_search'
    });
    setSelectedPrompt(null);
  };

  const getFilteredPrompts = () => {
    if (activeTab === 'active') {
      return prompts.filter(p => p.is_active);
    }
    return prompts;
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? theme.colors.status.success : theme.colors.status.error;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit', 
      day: '2-digit'
    }) + ', ' + date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ 
            color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
          }}>
            <FileText className="w-6 h-6" style={{ color: theme.colors.primary.main }} />
            Prompt Management
          </h1>
          <p className="text-sm mt-1" style={{ color: theme.colors.text.secondary }}>
            Manage LLM prompt templates for various services
          </p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => {
              resetForm();
              setModalType('create');
            }}
            className="px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 text-white" 
            style={{
              backgroundColor: theme.colors.primary.main,
            }}
          >
            <Plus className="w-4 h-4" />
            <span>Create New Prompt</span>
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 p-1 rounded-lg" style={{
        backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default,
      }}>
        <button
          onClick={() => setActiveTab('active')}
          className="flex-1 px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
          style={{
            backgroundColor: activeTab === 'active'
              ? darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper
              : 'transparent',
            color: activeTab === 'active'
              ? theme.colors.primary.main
              : darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary,
            boxShadow: activeTab === 'active' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
          }}
        >
          <Activity className="w-4 h-4" />
          <span>Active Prompts</span>
        </button>
        <button
          onClick={() => setActiveTab('all')}
          className="flex-1 px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
          style={{
            backgroundColor: activeTab === 'all'
              ? darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper
              : 'transparent',
            color: activeTab === 'all'
              ? theme.colors.primary.main
              : darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary,
            boxShadow: activeTab === 'all' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
          }}
        >
          <Layers className="w-4 h-4" />
          <span>All Versions</span>
        </button>
        <button
          onClick={() => setActiveTab('variables')}
          className="flex-1 px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
          style={{
            backgroundColor: activeTab === 'variables'
              ? darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper
              : 'transparent',
            color: activeTab === 'variables'
              ? theme.colors.primary.main
              : darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary,
            boxShadow: activeTab === 'variables' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
          }}
        >
          <Code2 className="w-4 h-4" />
          <span>Template Variables</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="rounded-xl" style={{
        backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
        boxShadow: theme.widget.boxShadow,
      }}>
        <div className="p-6">
          {activeTab !== 'variables' ? (
            loading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: theme.colors.primary.main }} />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Prompts Table Header */}
                <div className="grid grid-cols-12 gap-4 pb-3" style={{
                  borderBottom: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                }}>
                  <div className="col-span-1 text-sm font-medium" style={{ color: theme.colors.text.secondary }}>
                    Status
                  </div>
                  <div className="col-span-3 text-sm font-medium" style={{ color: theme.colors.text.secondary }}>
                    Name
                  </div>
                  <div className="col-span-2 text-sm font-medium" style={{ color: theme.colors.text.secondary }}>
                    Type
                  </div>
                  <div className="col-span-1 text-sm font-medium" style={{ color: theme.colors.text.secondary }}>
                    Version
                  </div>
                  <div className="col-span-2 text-sm font-medium" style={{ color: theme.colors.text.secondary }}>
                    Created By
                  </div>
                  <div className="col-span-2 text-sm font-medium" style={{ color: theme.colors.text.secondary }}>
                    Last Updated
                  </div>
                  <div className="col-span-1 text-sm font-medium text-center" style={{ color: theme.colors.text.secondary }}>
                    Actions
                  </div>
                </div>

                {/* Prompts List */}
                {getFilteredPrompts().map((prompt) => (
                  <div key={prompt.id} className="space-y-2">
                    <div className="grid grid-cols-12 gap-4 items-center py-3">
                      <div className="col-span-1">
                        <div className="flex items-center gap-2">
                          {prompt.is_active ? (
                            <CheckCircle className="w-5 h-5" style={{ color: theme.colors.status.success }} />
                          ) : (
                            <X className="w-5 h-5" style={{ color: theme.colors.status.error }} />
                          )}
                          <span className="text-xs px-2 py-1 rounded" style={{
                            backgroundColor: getStatusColor(prompt.is_active) + '20',
                            color: getStatusColor(prompt.is_active),
                          }}>
                            {prompt.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                      <div className="col-span-3">
                        <p className="font-medium" style={{ 
                          color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
                        }}>
                          {prompt.prompt_name}
                        </p>
                        {prompt.search_method && (
                          <p className="text-xs mt-1" style={{ color: theme.colors.text.secondary }}>
                            Method: {prompt.search_method}
                          </p>
                        )}
                      </div>
                      <div className="col-span-2">
                        <span className="text-sm px-2 py-1 rounded" style={{
                          backgroundColor: theme.colors.primary.light + '20',
                          color: theme.colors.primary.main,
                        }}>
                          {prompt.prompt_type.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="col-span-1">
                        <span className="text-sm" style={{ 
                          color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
                        }}>
                          v{prompt.version}
                        </span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-sm" style={{ 
                          color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary 
                        }}>
                          {prompt.created_by}
                        </span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-sm" style={{ 
                          color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary 
                        }}>
                          {formatDate(prompt.updated_at)}
                        </span>
                      </div>
                      <div className="col-span-1">
                        <div className="flex justify-center space-x-1">
                          <button 
                            onClick={() => openEditModal(prompt)}
                            className="p-1.5 rounded hover:bg-black hover:bg-opacity-5 transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" style={{ color: theme.colors.text.secondary }} />
                          </button>
                          <button 
                            onClick={() => setShowPromptText({
                              ...showPromptText,
                              [prompt.id]: !showPromptText[prompt.id]
                            })}
                            className="p-1.5 rounded hover:bg-black hover:bg-opacity-5 transition-colors"
                            title="View Prompt"
                          >
                            {showPromptText[prompt.id] ? (
                              <EyeOff className="w-4 h-4" style={{ color: theme.colors.text.secondary }} />
                            ) : (
                              <Eye className="w-4 h-4" style={{ color: theme.colors.text.secondary }} />
                            )}
                          </button>
                          <button 
                            onClick={() => openHistoryModal(prompt)}
                            className="p-1.5 rounded hover:bg-black hover:bg-opacity-5 transition-colors"
                            title="History"
                          >
                            <History className="w-4 h-4" style={{ color: theme.colors.text.secondary }} />
                          </button>
                          {prompt.is_active ? (
                            <button 
                              onClick={() => handleDeactivatePrompt(prompt.id)}
                              className="p-1.5 rounded hover:bg-black hover:bg-opacity-5 transition-colors"
                              title="Deactivate"
                            >
                              <X className="w-4 h-4" style={{ color: theme.colors.status.error }} />
                            </button>
                          ) : (
                            <button 
                              onClick={() => handleActivatePrompt(prompt.id)}
                              className="p-1.5 rounded hover:bg-black hover:bg-opacity-5 transition-colors"
                              title="Activate"
                            >
                              <Check className="w-4 h-4" style={{ color: theme.colors.status.success }} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Prompt Text */}
                    {showPromptText[prompt.id] && (
                      <div className="ml-4 p-4 rounded-lg" style={{
                        backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default,
                        border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                      }}>
                        <p className="text-sm font-medium mb-2" style={{ 
                          color: theme.colors.text.secondary 
                        }}>
                          Prompt Template:
                        </p>
                        <pre className="whitespace-pre-wrap text-sm" style={{
                          color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                          fontFamily: 'monospace',
                        }}>
                          {prompt.prompt_text}
                        </pre>
                      </div>
                    )}
                  </div>
                ))}

                {getFilteredPrompts().length === 0 && (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 mx-auto mb-4" style={{ 
                      color: theme.colors.text.secondary 
                    }} />
                    <p className="text-lg mb-2" style={{ 
                      color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
                    }}>
                      No prompts found
                    </p>
                    <p className="text-sm" style={{ 
                      color: theme.colors.text.secondary 
                    }}>
                      Create your first prompt to get started
                    </p>
                  </div>
                )}
              </div>
            )
          ) : (
            /* Template Variables Tab */
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ 
                  color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
                }}>
                  Available Template Variables
                </h3>
                <p className="text-sm mb-6" style={{ 
                  color: theme.colors.text.secondary 
                }}>
                  Use these variables in your prompts with double curly braces: {'{{variableName}}'}
                </p>

                {Object.entries(templateVariables).map(([type, variables]) => (
                  <div key={type} className="mb-8">
                    <h4 className="font-medium mb-4 px-3 py-2 rounded-lg inline-block" style={{
                      backgroundColor: theme.colors.primary.light + '20',
                      color: theme.colors.primary.main,
                    }}>
                      {type.replace('_', ' ').toUpperCase()}
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      {variables.map((variable) => (
                        <div key={variable.name} className="p-4 rounded-lg" style={{
                          backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default,
                          border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                        }}>
                          <code className="text-sm font-bold" style={{
                            color: theme.colors.primary.main,
                          }}>
                            {`{{${variable.name}}}`}
                          </code>
                          <p className="text-sm mt-2" style={{ 
                            color: theme.colors.text.secondary 
                          }}>
                            {variable.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {(modalType === 'create' || modalType === 'edit') && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
          <div className="rounded-2xl max-w-3xl w-full bg-white dark:bg-gray-800 shadow-2xl">
            <div className="p-6">
              <h2 className="text-2xl font-normal mb-6 text-gray-900 dark:text-white">
                {modalType === 'create' ? 'Create New Prompt' : 'Edit Prompt'}
              </h2>

              <div className="space-y-4">
                {/* Prompt Type */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ 
                    color: theme.colors.text.secondary 
                  }}>
                    Prompt Type
                  </label>
                  <select
                    value={formData.prompt_type}
                    onChange={(e) => setFormData({ ...formData, prompt_type: e.target.value })}
                    disabled={modalType === 'edit'}
                    className="w-full px-4 py-3 rounded-xl border"
                    style={{
                      backgroundColor: darkMode ? theme.colors.background.dark : '#FAFAFA',
                      borderColor: darkMode ? theme.colors.border.dark : theme.colors.border.light,
                      color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                    }}
                  >
                    {promptTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                {/* Prompt Name */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ 
                    color: theme.colors.text.secondary 
                  }}>
                    Prompt Name
                  </label>
                  <input
                    type="text"
                    value={formData.prompt_name}
                    onChange={(e) => setFormData({ ...formData, prompt_name: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border"
                    placeholder="Enter a descriptive name for this prompt"
                    style={{
                      backgroundColor: darkMode ? theme.colors.background.dark : '#FAFAFA',
                      borderColor: darkMode ? theme.colors.border.dark : theme.colors.border.light,
                      color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                    }}
                  />
                </div>

                {/* Search Method (for metrics_extraction) */}
                {formData.prompt_type === 'metrics_extraction' && (
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ 
                      color: theme.colors.text.secondary 
                    }}>
                      Search Method
                    </label>
                    <select
                      value={formData.search_method}
                      onChange={(e) => setFormData({ ...formData, search_method: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border"
                      style={{
                        backgroundColor: darkMode ? theme.colors.background.dark : '#FAFAFA',
                        borderColor: darkMode ? theme.colors.border.dark : theme.colors.border.light,
                        color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                      }}
                    >
                      <option value="advanced_search">Advanced Search</option>
                      <option value="web_tool">Web Tool</option>
                    </select>
                  </div>
                )}

                {/* Prompt Text */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ 
                    color: theme.colors.text.secondary 
                  }}>
                    Prompt Text
                  </label>
                  <textarea
                    value={formData.prompt_text}
                    onChange={(e) => setFormData({ ...formData, prompt_text: e.target.value })}
                    rows={12}
                    className="w-full px-4 py-3 rounded-xl border"
                    placeholder="Enter the prompt template text. Use {{variableName}} for template variables."
                    style={{
                      backgroundColor: darkMode ? theme.colors.background.dark : '#FAFAFA',
                      borderColor: darkMode ? theme.colors.border.dark : theme.colors.border.light,
                      color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                      fontFamily: 'monospace',
                      fontSize: '14px',
                    }}
                  />
                  <p className="text-xs mt-2" style={{ 
                    color: theme.colors.text.secondary 
                  }}>
                    Available variables for {formData.prompt_type}: {' '}
                    {templateVariables[formData.prompt_type as keyof typeof templateVariables]?.map(v => 
                      `{{${v.name}}}`
                    ).join(', ')}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setModalType(null);
                    resetForm();
                  }}
                  className="px-6 py-3 rounded-lg font-medium transition-all hover:opacity-80"
                  style={{
                    backgroundColor: 'transparent',
                    color: theme.colors.primary.main,
                    border: `2px solid ${theme.colors.primary.main}`,
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={modalType === 'create' ? handleCreatePrompt : handleUpdatePrompt}
                  className="px-6 py-3 rounded-lg font-medium text-white transition-all hover:opacity-90"
                  style={{
                    backgroundColor: theme.colors.primary.main,
                  }}
                >
                  {modalType === 'create' ? 'Create' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {modalType === 'history' && selectedPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
          <div className="rounded-2xl max-w-4xl w-full bg-white dark:bg-gray-800 shadow-2xl max-h-[80vh] overflow-hidden">
            <div className="p-6">
              <h2 className="text-2xl font-normal mb-6 text-gray-900 dark:text-white">
                Prompt History - {selectedPrompt.prompt_name}
              </h2>

              <div className="overflow-y-auto max-h-[60vh]">
                <table className="w-full">
                  <thead>
                    <tr style={{
                      borderBottom: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                    }}>
                      <th className="text-left py-3 px-4 text-sm font-medium" style={{ 
                        color: theme.colors.text.secondary 
                      }}>
                        Version
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium" style={{ 
                        color: theme.colors.text.secondary 
                      }}>
                        Action
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium" style={{ 
                        color: theme.colors.text.secondary 
                      }}>
                        Changed By
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium" style={{ 
                        color: theme.colors.text.secondary 
                      }}>
                        Date
                      </th>
                      <th className="text-center py-3 px-4 text-sm font-medium" style={{ 
                        color: theme.colors.text.secondary 
                      }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {promptHistory.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-8 text-center" style={{ 
                          color: theme.colors.text.secondary 
                        }}>
                          No history entries found
                        </td>
                      </tr>
                    ) : (
                      promptHistory.map((history) => (
                        <tr key={history.id} style={{
                          borderBottom: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                        }}>
                          <td className="py-3 px-4">
                            <span className="font-medium" style={{ 
                              color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
                            }}>
                              v{history.version}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm" style={{ 
                              color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary 
                            }}>
                              {history.action}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm" style={{ 
                              color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary 
                            }}>
                              {history.changed_by}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm" style={{ 
                              color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary 
                            }}>
                              {formatDate(history.created_at)}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex justify-center">
                              {history.version !== selectedPrompt.version && (
                                <button
                                  onClick={() => handleRollback(history.version)}
                                  className="p-1.5 rounded hover:bg-black hover:bg-opacity-5 transition-colors"
                                  title="Rollback to this version"
                                >
                                  <RotateCcw className="w-4 h-4" style={{ 
                                    color: theme.colors.primary.main 
                                  }} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => {
                    setModalType(null);
                    setPromptHistory([]);
                  }}
                  className="px-6 py-3 rounded-lg font-medium transition-all hover:opacity-80"
                  style={{
                    backgroundColor: theme.colors.primary.main,
                    color: '#FFFFFF',
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPrompts;